// "El servidor no contesta ahora mismo" — un solo sitio que lo sabe.
//
// ── EL PROBLEMA ────────────────────────────────────────────────────────────
//
// Lissette, 2026-09-01: "cuando estamos haciendo actualizaciones, a mis
// clientes se les cuelga por un minuto y se quejan: esto no abre".
//
// Cuando sube una version nueva del servidor hay una ventana en la que el
// contenedor viejo ya se fue y el nuevo todavia esta arrancando (carga las
// contraseñas de Azure ANTES de empezar a contestar). Esa ventana dura
// bastante mas que un parpadeo.
//
// `apiFetch` ya reintentaba, pero con un presupuesto de 7,7 segundos — pensado
// para un reinicio corto (2026-08-20). Un despliegue dura mucho mas, asi que
// quien hacia clic en ese momento se quedaba fuera del reintento: aviso rojo y
// pantalla vacia.
//
// ── LO QUE CAMBIA ──────────────────────────────────────────────────────────
//
// 1. El presupuesto de reintentos pasa a cubrir un despliegue entero.
// 2. Mientras se reintenta, la pantalla lo DICE con un aviso tranquilo en vez
//    de quedarse muda o roja. Esto vive aqui, fuera de React, porque quien se
//    entera primero es `apiFetch` — que no es un componente.
//
// Un aviso que aparece a los 2 segundos y se va solo vale mas que uno rojo al
// final: la persona sabe que no esta rota y que no tiene que hacer nada.

// Las esperas entre intento e intento, en milisegundos.
//
// Suman ~88 segundos. Parece muchisimo para un clic, y lo seria si pasara a
// menudo: solo ocurre cuando la alternativa es una lista vacia para siempre.
// Las tres primeras son cortas a proposito (un tropiezo de red se arregla
// ahi); las largas son las que aguantan el despliegue.
//
// Las llamadas de una pantalla salen A LA VEZ, asi que la espera total que
// nota la persona es esta, no esta multiplicada por el numero de llamadas.
export const ESPERAS_DE_REINTENTO = [700, 2000, 5000, 8000, 12000, 15000, 20000, 25000];

/** Cuanto se aguanta en total, si todos los intentos fallan. */
export function presupuestoDeEspera(esperas = ESPERAS_DE_REINTENTO) {
  return esperas.reduce((a, b) => a + b, 0);
}

// ── El estado que ve la pantalla ───────────────────────────────────────────

let reintentando = false;
let desdeCuando = 0;
const oyentes = new Set();

function avisar() {
  for (const fn of oyentes) {
    try { fn(reintentando); } catch { /* un oyente roto no puede tumbar a los demas */ }
  }
}

/** Una llamada se topo con el servidor caido y va a reintentar. */
export function marcarReintentando() {
  if (reintentando) return;
  reintentando = true;
  desdeCuando = Date.now();
  avisar();
}

/**
 * El servidor volvio a contestar.
 *
 * Lo llama CUALQUIER respuesta buena, no solo la que fallo: si otra llamada ya
 * esta pasando, el servidor esta vivo y el aviso sobra. Sin esto el aviso se
 * quedaria puesto hasta que terminara el reintento mas lento.
 */
export function marcarServidorVivo() {
  if (!reintentando) return;
  reintentando = false;
  desdeCuando = 0;
  avisar();
}

export function estaReintentando() {
  return reintentando;
}

/** Desde cuando, en milisegundos. 0 si ahora mismo no se esta reintentando. */
export function reintentandoDesde() {
  return reintentando ? desdeCuando : 0;
}

/** Se suscribe a los cambios. Devuelve la funcion para darse de baja. */
export function suscribirseAlServidor(fn) {
  if (typeof fn !== 'function') return () => {};
  oyentes.add(fn);
  return () => { oyentes.delete(fn); };
}

// Solo para las pruebas: deja el estado como recien cargado.
export function _reiniciarEstadoDelServidor() {
  reintentando = false;
  desdeCuando = 0;
  oyentes.clear();
}
